<script setup>
import { ref } from 'vue'
import router from '@/router'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems
} from '@headlessui/vue'
import { 
  Bars3Icon, 
  XMarkIcon,
  ClipboardDocumentListIcon,
  ShoppingBagIcon,
  Cog6ToothIcon,
  KeyIcon,
  GiftIcon,
  CalendarDaysIcon,
  UserPlusIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/vue/24/outline'
import { useAppNavigation } from '@/composables/AppNavigation'
import ThemeToggle from '@/components/ThemeToggle.vue'

const restoStore = useRestoGLobalStore()
const { getNavLinks } = useAppNavigation()
let open = ref(false)

// Map icon names to components
const iconMap = {
  ClipboardDocumentListIcon,
  ShoppingBagIcon,
  Cog6ToothIcon,
  KeyIcon,
  GiftIcon,
  CalendarDaysIcon,
  UserPlusIcon,
  ArrowRightOnRectangleIcon
}
</script>

<template>
  <!--
    This example requires updating your template:

    ```
    <html class="h-full">
    <body class="h-full">
    ```
  -->
  <header
    :class="[
      open ? 'sticky top-0 z-20 overflow-y-auto pt-safe' : '',
      'sticky top-0 w-full z-20 mx-auto mb-0 border-b bg-gradient-to-r from-white to-gray-50 shadow-md lg:overflow-y-visible pt-safe dark:from-gray-900 dark:to-gray-800 dark:border-gray-700 transition-all duration-300'
    ]"
  >
    <div class="min-h-full">
      <Disclosure as="nav" class="bg-transparent" v-slot="{ open }">
        <div class="mx-auto px-4 sm:px-4 lg:px-6 py-1">
          <div class="flex h-16 justify-between items-center">
            <div class="flex">
              <div class="flex flex-shrink-0 items-center">
                <RouterLink :to="{ name: 'restoHome' }" class="transition-transform duration-300 hover:scale-105">
                  <img
                    class="block h-10 w-auto lg:hidden rounded-md"
                    :src="restoStore.restoInfo.logo"
                    :alt="restoStore.restoInfo.name"
                  />
                  <img
                    class="hidden h-10 w-auto lg:block rounded-md"
                    :src="restoStore.restoInfo.logo"
                    :alt="restoStore.restoInfo.name"
                  />
                </RouterLink>
              </div>
              <div class="hidden sm:-my-px lg:ml-6 sm:ml-4 md:flex sm:space-x-2 md:space-x-3 lg:space-x-6">
                <RouterLink
                  v-for="item in getNavLinks()"
                  :key="item.name"
                  :to="{ name: item.route_name, params: item.params, query: item.query }"
                  :class="[
                    router.currentRoute.value.name === item.route_name
                      ? 'border-indigo-500 text-indigo-600 dark:text-indigo-300'
                      : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white',
                    'inline-flex items-center border-b-2 lg:px-2 px-1 pt-1 text-sm font-medium transition-all duration-200 hover:scale-105'
                  ]"
                  :aria-current="
                    router.currentRoute.value.name === item.route_name ? 'page' : undefined
                  "
                >
                  <component 
                    v-if="item.icon && iconMap[item.icon]" 
                    :is="iconMap[item.icon]" 
                    class="lg:mr-1.5 mr-1 h-5 w-5 transition-colors" 
                    :class="router.currentRoute.value.name === item.route_name ? 'text-indigo-500 dark:text-indigo-300' : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-300'"
                    aria-hidden="true" 
                  />
                  {{ item.name }}
                </RouterLink>
              </div>
            </div>
            <div class="hidden sm:ml-2 lg:ml-6 sm:flex sm:items-center">
              <!-- Theme Toggle -->
              <ThemeToggle class="mr-1 transition-transform duration-200 hover:scale-110" />

              <!-- Profile dropdown -->
              <Menu as="div" class="relative ml-3">
                <div>
                  <MenuButton
                    class="relative flex max-w-xs items-center rounded-full bg-white/80 p-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:bg-gray-700/80 dark:focus:ring-offset-gray-800 transition-all duration-300 hover:shadow-md"
                  >
                    <span class="sr-only">Open menu</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                      class="h-6 w-6 text-gray-600 dark:text-gray-300"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                      />
                    </svg>
                  </MenuButton>
                </div>
                <transition
                  enter-active-class="transition ease-out duration-200"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems
                    class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white/95 dark:bg-gray-800/95 py-2 shadow-xl ring-1 ring-black/5 focus:outline-none backdrop-blur-sm"
                  >
                    <MenuItem v-for="item in getNavLinks()" :key="item.name" v-slot="{ active }">
                      <RouterLink
                        :to="{ name: item.route_name, params: item.params, query: item.query }"
                        :class="[
                          active ? 'bg-indigo-50 dark:bg-indigo-900/30' : '',
                          'block px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors duration-150'
                        ]"
                      >
                        <div class="flex items-center">
                          <component 
                            v-if="item.icon && iconMap[item.icon]" 
                            :is="iconMap[item.icon]" 
                            class="mr-2.5 h-5 w-5 text-indigo-500 dark:text-indigo-400" 
                            aria-hidden="true" 
                          />
                          <span class="font-medium">{{ item.name }}</span>
                        </div>
                      </RouterLink>
                    </MenuItem>
                  </MenuItems>
                </transition>
              </Menu>
            </div>
            <div class="-mr-2 flex items-center sm:hidden">
              <!-- Mobile Theme Toggle -->
              <ThemeToggle class="mr-2 transition-transform duration-200 hover:scale-110" />
              
              <!-- Mobile menu button -->
              <DisclosureButton
                class="relative inline-flex items-center justify-center rounded-md bg-white/80 dark:bg-gray-700/80 p-2 text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-all duration-200"
              >
                <span class="sr-only">Open main menu</span>
                <Bars3Icon v-if="!open" class="block h-6 w-6" aria-hidden="true" />
                <XMarkIcon v-else class="block h-6 w-6" aria-hidden="true" />
              </DisclosureButton>
            </div>
          </div>
        </div>

        <DisclosurePanel class="sm:hidden bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm">
          <div class="space-y-1 pb-3 pt-2 px-1">
            <RouterLink
              v-for="item in getNavLinks()"
              :key="item.name"
              :to="{ name: item.route_name, params: item.params, query: item.query }"
              @click="open = !open"
              :class="[
                router.currentRoute.value.name === item.route_name
                  ? 'border-indigo-500 bg-indigo-50/80 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                  : 'border-transparent text-gray-600 dark:text-gray-300 hover:border-indigo-300 hover:bg-indigo-50/50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-300',
                'block border-l-4 py-2.5 pl-3 pr-4 text-base font-medium flex items-center rounded-r-md transition-all duration-200'
              ]"
              :aria-current="
                router.currentRoute.value.name === item.route_name ? 'page' : undefined
              "
            >
              <component 
                v-if="item.icon && iconMap[item.icon]" 
                :is="iconMap[item.icon]" 
                class="mr-3 h-5 w-5 flex-shrink-0" 
                :class="router.currentRoute.value.name === item.route_name ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-400 group-hover:text-indigo-400'"
                aria-hidden="true" 
              />
              {{ item.name }}
            </RouterLink>
          </div>
        </DisclosurePanel>
      </Disclosure>
    </div>
  </header>
</template>

<style>
/* Add some global styles for the header */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.router-link-active .icon {
  animation: fadeIn 0.3s ease-in-out;
}
</style>
